import { getAuth } from 'firebase/auth';
import { collection, doc, getDoc, getDocs, getFirestore, query, runTransaction, where } from 'firebase/firestore';
import { applyVitastrongAssignment, getIntegrationSignature, hasLegacyIntegration, mergeProtectedProgramLists, preserveIntegrationFields, serializeIntegrationValue } from './integrazione';
import { INVII_ROOT_FIELDS, snapshotInviiPendingChanges } from './inviiPendingChanges';
import { buildLatestProgramMirror } from './latestProgramMirror';

const entriesOf = (snapshot) => snapshot.exists() && Array.isArray(snapshot.data()?.alimentazioni) ? snapshot.data().alimentazioni : [];
const entryOf = (snapshot, id) => {
  const matches = entriesOf(snapshot).filter((entry) => entry?.id === id);
  if (matches.length > 1) throw new Error('Identificativo alimentazione duplicato: è necessaria una verifica prima di modificare lo storico.');
  return matches[0];
};

// Invii keeps its existing lightweight list query. Additional reads happen only on user action.
async function getActiveMirrorRefs(db, userId, inviiDocId) {
  const snapshot = await getDocs(query(collection(db, 'inviidigiornata'), where('uid', '==', userId)));
  const refs = new Map(snapshot.docs.map((item) => [item.id, item.ref]));
  if (inviiDocId) refs.set(inviiDocId, doc(db, 'inviidigiornata', inviiDocId));
  return [...refs.values()];
}

function resolveEntry(canonicalEntry, mirrorEntries) {
  if (!canonicalEntry) throw new Error('Alimentazione non ancora salvata o non più disponibile. Ricarica la pagina.');
  // An assignment found in any existing copy prevents reinterpreting legacy history.
  const entries = [canonicalEntry, ...mirrorEntries.filter(Boolean)];
  const legacy = entries.find(hasLegacyIntegration);
  if (legacy) return preserveIntegrationFields(canonicalEntry, legacy);
  const snapshots = entries.filter((entry) => entry.integrazioneAssignment || entry.integrazioneHistory?.length);
  const versions = new Set(snapshots.map((entry) => serializeIntegrationValue([
    entry.integrazioneAssignment || null,
    entry.integrazioneHistory || [],
  ])));
  if (versions.size > 1) {
    throw new Error('Le copie di questa integrazione non coincidono. Nessuno storico verrà sovrascritto: richiedi una verifica.');
  }
  // A cleared assignment still carries history that must survive future edits.
  const saved = snapshots[0];
  return saved ? preserveIntegrationFields(canonicalEntry, saved) : canonicalEntry;
}

export async function loadIntegrationEntry({ userId, alimentazioneId, inviiDocId = '' }) {
  if (!userId || !alimentazioneId) throw new Error('Utente o alimentazione mancanti.');
  const db = getFirestore();
  const [canonical, querySnapshot] = await Promise.all([
    getDoc(doc(db, 'alimentazioni', userId)),
    getDocs(query(collection(db, 'inviidigiornata'), where('uid', '==', userId))),
  ]);
  // The query already contains the mirror data: do not read every document twice.
  const mirrors = [...querySnapshot.docs];
  if (inviiDocId && !mirrors.some((snapshot) => snapshot.id === inviiDocId)) {
    mirrors.push(await getDoc(doc(db, 'inviidigiornata', inviiDocId)));
  }
  const validMirrors = mirrors.filter((snapshot) => snapshot.exists() && snapshot.data().uid === userId);
  const entry = resolveEntry(entryOf(canonical, alimentazioneId), validMirrors.map((snapshot) => entryOf(snapshot, alimentazioneId)));
  return { entry, signature: getIntegrationSignature(entry) };
}

export async function saveIntegrationAssignment({ userId, alimentazioneId, bundleIds, bundleId, expectedSignature, inviiDocId = '', pendingChanges }) {
  const combined = pendingChanges !== undefined;
  const committedChanges = snapshotInviiPendingChanges(pendingChanges);
  const rootPatch = {};
  const programPatches = new Map();
  for (const [key, value] of Object.entries(committedChanges)) {
    if (INVII_ROOT_FIELDS.includes(key)) rootPatch[key] = value === '' ? null : value;
    else programPatches.set(key, Object.fromEntries(Object.entries(value).map(([field, fieldValue]) => [field, fieldValue === '' ? null : fieldValue])));
  }
  if (combined && !inviiDocId) throw new Error('Invio corrente mancante: impossibile salvare le modifiche.');
  const actorId = getAuth().currentUser?.uid;
  if (!actorId) throw new Error('Accedi nuovamente per salvare l’integrazione.');
  if (!userId || !alimentazioneId) throw new Error('Utente o alimentazione mancanti.');
  const db = getFirestore();
  const canonicalRef = doc(db, 'alimentazioni', userId);
  const mirrorRefs = await getActiveMirrorRefs(db, userId, inviiDocId);
  const now = new Date().toISOString();
  return runTransaction(db, async (transaction) => {
    const [actor, canonical, ...mirrors] = await Promise.all([
      transaction.get(doc(db, 'users', actorId)),
      transaction.get(canonicalRef),
      ...mirrorRefs.map((ref) => transaction.get(ref)),
    ]);
    if (!actor.exists() || !['Amministratore', 'Studio'].includes(actor.data().userRole)) {
      throw new Error('Non hai i permessi per assegnare l’integrazione.');
    }
    if (combined && actor.data().userRole !== 'Amministratore') {
      throw new Error('Solo un Amministratore può salvare le modifiche Invii.');
    }
    const validMirrors = mirrors.filter((snapshot) => snapshot.exists() && snapshot.data().uid === userId);
    const currentMirror = combined ? validMirrors.find((snapshot) => snapshot.id === inviiDocId) : null;
    if (combined) {
      if (!currentMirror) throw new Error('Invio corrente non più disponibile o associato a un altro utente. Ricarica la pagina.');
      if (canonical.exists() && canonical.data().userId && canonical.data().userId !== userId) {
        throw new Error('Alimentazioni associate a un altro utente. Nessuna modifica salvata.');
      }
      for (const id of new Set([alimentazioneId, ...programPatches.keys()])) {
        if (!entryOf(canonical, id) || !entryOf(currentMirror, id)) {
          throw new Error(`Alimentazione ${id} non più disponibile in entrambe le copie. Ricarica la pagina.`);
        }
      }
    }
    const current = resolveEntry(entryOf(canonical, alimentazioneId), validMirrors.map((snapshot) => entryOf(snapshot, alimentazioneId)));
    const entry = applyVitastrongAssignment(current, bundleIds ?? bundleId, actorId, now, expectedSignature);
    const integrationChanged = entry !== current;
    const programsChanged = programPatches.size > 0;
    const hasRootPatch = Object.keys(rootPatch).length > 0;
    if (!integrationChanged && !programsChanged && !hasRootPatch) {
      return combined ? { entry, committedChanges, inviiPatch: {} } : { entry };
    }
    const patchEntries = (snapshot, includePending) => entriesOf(snapshot).map((item) => {
      const patch = includePending ? programPatches.get(item.id) : null;
      const next = patch ? { ...item, ...patch } : item;
      return integrationChanged && item.id === alimentazioneId ? preserveIntegrationFields(next, entry) : next;
    });
    const canonicalEntries = patchEntries(canonical, true);
    transaction.update(canonicalRef, {
      ...rootPatch,
      ...(integrationChanged || programsChanged ? { alimentazioni: canonicalEntries } : {}),
    });
    let inviiPatch = {};
    for (const mirror of validMirrors) {
      const isCurrentMirror = mirror === currentMirror;
      if (!isCurrentMirror && (!integrationChanged || !entryOf(mirror, alimentazioneId))) continue;
      // Each copy starts from its own transaction snapshot; only the current Invii
      // receives pending fields. Other active mirrors receive the selected assignment.
      const entries = patchEntries(mirror, isCurrentMirror);
      const patch = {
        ...(isCurrentMirror ? rootPatch : {}),
        ...(integrationChanged || (isCurrentMirror && programsChanged) ? { alimentazioni: entries } : {}),
        ...(isCurrentMirror && programsChanged ? buildLatestProgramMirror(entries) : {}),
      };
      transaction.update(mirror.ref, patch);
      if (isCurrentMirror) inviiPatch = patch;
    }
    return combined ? {
      entry: preserveIntegrationFields(canonicalEntries.find((item) => item.id === alimentazioneId), entry),
      committedChanges,
      inviiPatch,
    } : { entry };
  });
}

// Other editor actions cannot replace integration with a stale local value.
export async function saveProtectedAlimentazioni({ userId, entries, rootPatch = {}, baseEntries }) {
  if (!userId || !Array.isArray(entries)) throw new Error('Dati alimentazione non validi.');
  const db = getFirestore();
  const ref = doc(db, 'alimentazioni', userId);
  return runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(ref);
    const next = mergeProtectedProgramLists(entriesOf(snapshot), entries, baseEntries);
    transaction.set(ref, { ...rootPatch, userId, alimentazioni: next }, { merge: true });
    return next;
  });
}

// Protect assignments during independent file, flag and add-program operations.
export async function updateProtectedProgramDocument(ref, patch) {
  return runTransaction(getFirestore(), async (transaction) => {
    const snapshot = await transaction.get(ref);
    const entries = mergeProtectedProgramLists(entriesOf(snapshot), patch.alimentazioni || []);
    transaction.set(ref, { ...patch, alimentazioni: entries }, { merge: true });
    return entries;
  });
}
